import type { SupabaseClient } from '@supabase/supabase-js';
import { classLocalToday, normalizeFirstClassDate } from '@/utils/classes/ongoingClassStart';
import { shiftBillingMonth } from '@/utils/billing/billMonthAhead';

/**
 * #4724 PR 2 — what an enrollment made BEFORE an Ongoing Class's First class
 * date is billed, and when.
 *
 * Owner decisions (Geng, 2026-09-10 — do not re-open):
 *  P1  The first-class month bills at the FULL monthly rate, no proration.
 *  P2  Tuition is deferred (nothing today) ONLY when the monthly run can
 *      collect it later: the gym has automatic monthly invoicing on AND the
 *      family enrolls before the gym's send day for the first-class month.
 *      Otherwise the first-class month is charged at checkout, full rate.
 *  P3  The annual gym membership fee is still charged at sign-up.
 *  P4  The run's first month is the LATER of the month promised at checkout
 *      (program_enrollments.billing_starts_month) and the class's live
 *      first-class month — never earlier.
 *
 * Every date here is a 'YYYY-MM-DD' string and every month a 'YYYY-MM' string,
 * compared lexically. Callers resolve gym-local "today" themselves (the class
 * location's day for "has the class started", the org's billing day for the
 * send-day compare — the same clock the monthly run keeps).
 */

export interface FirstClassBillingPlanInput {
  /** classes.first_class_date (null/invalid ⇒ today's behaviour exactly). */
  firstClassDate: string | null | undefined;
  /** Gym-local today at the CLASS's location — decides "before the first class". */
  classLocalToday: string;
  /** Gym-local today on the org's billing clock — decides "before the send day". */
  orgLocalToday: string;
  /**
   * The CLASSES stream — master switch AND the Classes box
   * (`resolveOrgAutoInvoiceStreams(org).classEnrollments`), never the raw
   * master: the deferral trusts the monthly run to bill the class month.
   */
  autoInvoiceEnabled: boolean;
  /** organizations.auto_invoice_send_on_day (1–28). Anything else ⇒ cannot defer. */
  autoInvoiceSendOnDay: number | string | null | undefined;
  /**
   * #5694: the gym bills one month ahead — the run that bills the first-class
   * month is the one in the month BEFORE it, so the deferral holds only until
   * that run's send day.
   */
  billMonthAhead?: boolean;
}

export interface FirstClassBillingPlan {
  /** The first class is after gym-local today. False ⇒ nothing below applies. */
  preStart: boolean;
  firstClassDate: string | null;
  /** 'YYYY-MM' of the first class. Null unless preStart. */
  firstClassMonth: string | null;
  /** True ⇒ no tuition today; the monthly run bills firstClassMonth (P2). */
  deferred: boolean;
  /** The org send day the deferral relies on. Null unless deferred. */
  sendOnDay: number | null;
  /**
   * #5694: 'YYYY-MM' of the run that bills firstClassMonth — the month BEFORE it.
   * Present only when deferred at a gym that bills one month ahead.
   */
  sendMonth?: string;
}

function coerceSendDay(value: number | string | null | undefined): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(n) || n < 1 || n > 28) return null;
  return n;
}

export function resolveFirstClassBillingPlan(input: FirstClassBillingPlanInput): FirstClassBillingPlan {
  const first = normalizeFirstClassDate(input.firstClassDate);
  const notPreStart: FirstClassBillingPlan = {
    preStart: false,
    firstClassDate: first,
    firstClassMonth: null,
    deferred: false,
    sendOnDay: null,
  };
  if (!first || !(input.classLocalToday < first)) return notPreStart;

  const firstClassMonth = first.slice(0, 7);
  const sendOnDay = coerceSendDay(input.autoInvoiceSendOnDay);
  if (!input.autoInvoiceEnabled || sendOnDay === null) {
    return { preStart: true, firstClassDate: first, firstClassMonth, deferred: false, sendOnDay: null };
  }
  const sendMonth = input.billMonthAhead === true ? shiftBillingMonth(firstClassMonth, -1) : firstClassMonth;
  const sendDate = `${sendMonth}-${String(sendOnDay).padStart(2, '0')}`;
  const deferred = input.orgLocalToday < sendDate;
  return {
    preStart: true,
    firstClassDate: first,
    firstClassMonth,
    deferred,
    sendOnDay: deferred ? sendOnDay : null,
    ...(deferred && input.billMonthAhead === true ? { sendMonth } : {}),
  };
}

/** P2 as a yes/no. */
export function shouldDeferFirstBill(input: FirstClassBillingPlanInput): boolean {
  return resolveFirstClassBillingPlan(input).deferred;
}

/**
 * P4: the later of two 'YYYY-MM' months; a missing side yields the other.
 * Both missing ⇒ null (no floor).
 */
export function laterOfMonths(a: string | null | undefined, b: string | null | undefined): string | null {
  const va = a && /^\d{4}-\d{2}$/.test(a) ? a : null;
  const vb = b && /^\d{4}-\d{2}$/.test(b) ? b : null;
  if (!va) return vb;
  if (!vb) return va;
  return va >= vb ? va : vb;
}

/**
 * 'YYYY-MM' of a 'YYYY-MM-DD' first class date, for the run's live floor.
 * Null when the date is missing or malformed.
 */
export function monthOfFirstClassDate(firstClassDate: string | null | undefined): string | null {
  const first = normalizeFirstClassDate(firstClassDate);
  return first ? first.slice(0, 7) : null;
}

/**
 * The enrollment-row columns a pre-start checkout writes. Deferred:
 * `first_billed_month` is NULL on purpose (no month was billed) and the
 * paid-checkout path must carry `pre_start: true` through invoice metadata so
 * BillingService.handleInvoicePaidSideEffects (CASE E) keeps that NULL instead
 * of falling back to the enrollment date's month. Charged at checkout: the
 * first-class month is the billed month, and the promise is recorded too so
 * the run's floor holds if the class date later moves.
 */
export function preStartEnrollmentColumns(plan: FirstClassBillingPlan): {
  first_billed_month: string | null;
  billing_starts_month: string;
} | null {
  if (!plan.preStart || !plan.firstClassMonth) return null;
  return {
    first_billed_month: plan.deferred ? null : plan.firstClassMonth,
    billing_starts_month: plan.firstClassMonth,
  };
}

/**
 * The metadata marker the paid checkout puts on each enrollment_details entry
 * for CASE E. Readers test `pre_start === true`, never the presence of the
 * month alone.
 */
export function preStartEnrollmentMetadata(plan: FirstClassBillingPlan): {
  pre_start: true;
  billing_starts_month: string;
  first_billed_month: string | null;
} | null {
  const cols = preStartEnrollmentColumns(plan);
  if (!cols) return null;
  return { pre_start: true, ...cols };
}

/** R15: the coupon refusal on a deferred pre-start checkout (before any use is reserved). */
export const ONE_TIME_COUPON_BEFORE_FIRST_CLASS_MESSAGE = "This code can't be used before the first class date.";

/**
 * The monthly run's months for a RE-CONFIRMED enrollment (cancelled_at set,
 * status flipped back to confirmed): the current month only, never back-billed
 * — unless the enrollment's floor (P4) is still ahead, in which case nothing.
 */
export function reconfirmedEnrollmentMonths(billingFloorMonth: string | null | undefined, currentMonth: string): string[] {
  if (billingFloorMonth && billingFloorMonth > currentMonth) return [];
  return [currentMonth];
}

/** The 400 both enrollment routes answer when a fixed_term request lands on an Ongoing Class. */
export const FIXED_TERM_NOT_OFFERED_MESSAGE = "This class doesn't offer session enrollment.";

/**
 * What ensureRequiredClassAutopay needs to know about a pre-start enrollment:
 * the first-class month and whether it was charged at checkout. Deferred ⇒ the
 * next charge is that month; charged ⇒ the month after (the normal next cycle).
 */
export interface PreStartAutopayContext {
  firstBillMonth: string;
  deferred: boolean;
}

export function preStartAutopayContext(plan: FirstClassBillingPlan): PreStartAutopayContext | null {
  if (!plan.preStart || !plan.firstClassMonth) return null;
  return { firstBillMonth: plan.firstClassMonth, deferred: plan.deferred };
}

/**
 * The charge gate's copy of the checkout's plan resolution: the class row's
 * date, the org's auto-invoice settings and the class-location clock, all
 * server-resolved. Null for an undated class (today's derivation exactly).
 */
export async function resolveFirstClassPlanForOrder(input: {
  supabase: SupabaseClient;
  organizationId: string;
  locationId: string | null;
  firstClassDate: string | null | undefined;
  orgLocalToday: string;
  autoInvoiceEnabled: boolean;
  autoInvoiceSendOnDay: number | string | null | undefined;
  billMonthAhead?: boolean;
}): Promise<FirstClassBillingPlan | null> {
  if (!normalizeFirstClassDate(input.firstClassDate)) return null;
  return resolveFirstClassBillingPlan({
    firstClassDate: input.firstClassDate,
    classLocalToday: await classLocalToday(input.supabase, input.organizationId, input.locationId),
    orgLocalToday: input.orgLocalToday,
    autoInvoiceEnabled: input.autoInvoiceEnabled,
    autoInvoiceSendOnDay: input.autoInvoiceSendOnDay,
    billMonthAhead: input.billMonthAhead === true,
  });
}
